import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import RichTextEditor from '../../components/editor/RichTextEditor';
import {
  NEWS_HOME_SHOW_OPTIONS,
  NEWS_STATE,
  NEWS_STATE_OPTIONS,
  NEWS_CATEGORY_OPTIONS,
  formatNewsCategory,
  formatNewsProvider,
  formatNewsSource,
  fromDateTimeInput,
  mergeDetailsWithLanguages,
  stripDetailMeta,
  toDateTimeInput,
} from './newsConstants';

const CREATE_CATEGORY_OPTIONS = NEWS_CATEGORY_OPTIONS.filter((o) => o.value);

const EMPTY_FORM = {
  sort: 1,
  state: NEWS_STATE.ON,
  isHomeShow: 1,
  newsTimeInput: '',
  source: 'ADMIN',
  category: 'EXCHANGE',
  provider: 'MANUAL',
  details: [],
};

export default function NewsEditModal({ open, newsId, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const isEdit = Boolean(newsId);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const langList = await adminApi.newsLanguages();
        if (cancelled) {
          return;
        }
        setLanguages(langList || []);
        if (newsId) {
          const data = await adminApi.getNews(newsId);
          if (cancelled) {
            return;
          }
          setForm({
            sort: data.sort ?? 1,
            state: data.state ?? NEWS_STATE.ON,
            isHomeShow: data.isHomeShow ?? 1,
            newsTimeInput: toDateTimeInput(data.newsTime),
            source: data.source || 'ADMIN',
            category: data.category || 'EXCHANGE',
            provider: data.provider || 'MANUAL',
            createTime: data.createTime,
            details: mergeDetailsWithLanguages(langList, data.details),
          });
        } else {
          const now = new Date();
          const pad = (n) => String(n).padStart(2, '0');
          const nowInput = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
          setForm({
            ...EMPTY_FORM,
            newsTimeInput: nowInput,
            details: mergeDetailsWithLanguages(langList, []),
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || '載入失敗');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, newsId]);

  function updateDetail(index, key, value) {
    setForm((prev) => {
      const nextDetails = [...prev.details];
      nextDetails[index] = { ...nextDetails[index], [key]: value };
      return { ...prev, details: nextDetails };
    });
  }

  async function handleImageUpload(index, event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      const url = await adminApi.uploadOssImage(file);
      updateDetail(index, 'image', url);
    } catch (err) {
      setError(err.message || '圖片上傳失敗');
    } finally {
      event.target.value = '';
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    const newsTime = fromDateTimeInput(form.newsTimeInput);
    if (!newsTime) {
      setError('請填寫新聞時間');
      return;
    }
    const payload = {
      sort: Number(form.sort),
      state: Number(form.state),
      isHomeShow: Number(form.isHomeShow),
      newsTime,
      details: stripDetailMeta(form.details).filter((d) => d.title?.trim() && d.body?.trim()),
    };
    if (payload.details.length === 0) {
      setError('至少填寫一個語系的標題與內容');
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        await adminApi.updateNews({ id: newsId, ...payload });
      } else {
        await adminApi.createNews({
          ...payload,
          category: form.category,
          provider: form.provider,
        });
      }
      onSaved?.(isEdit);
      onClose?.();
    } catch (err) {
      setError(err.message || '保存失敗');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <div className="confirm-dialog-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__header">
          <h3>{isEdit ? '修改新聞資訊' : '新增新聞資訊'}</h3>
          <button type="button" className="btn btn-link" onClick={onClose}>
            關閉
          </button>
        </div>

        {loading ? (
          <p className="table-empty">載入中…</p>
        ) : (
          <form className="modal-panel__body news-edit-form" onSubmit={handleSubmit}>
            <div className="form-grid form-grid--2">
              <div className="form-field">
                <label htmlFor="newsSort">顯示順序 *</label>
                <input
                  id="newsSort"
                  type="number"
                  min="0"
                  value={form.sort}
                  onChange={(e) => setForm((p) => ({ ...p, sort: e.target.value }))}
                  required
                />
              </div>
              <div className="form-field">
                <label htmlFor="newsTime">新聞時間 *</label>
                <input
                  id="newsTime"
                  type="datetime-local"
                  value={form.newsTimeInput}
                  onChange={(e) => setForm((p) => ({ ...p, newsTimeInput: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="form-field">
              <span className="form-label">狀態 *</span>
              <div className="radio-row">
                {NEWS_STATE_OPTIONS.filter((o) => o.value !== '').map((opt) => (
                  <label key={opt.value} className="radio-inline">
                    <input
                      type="radio"
                      name="newsState"
                      value={opt.value}
                      checked={String(form.state) === opt.value}
                      onChange={() => setForm((p) => ({ ...p, state: Number(opt.value) }))}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-field">
              <span className="form-label">是否在首頁顯示 *</span>
              <div className="radio-row">
                {NEWS_HOME_SHOW_OPTIONS.filter((o) => o.value !== '').map((opt) => (
                  <label key={opt.value} className="radio-inline">
                    <input
                      type="radio"
                      name="newsHomeShow"
                      value={opt.value}
                      checked={String(form.isHomeShow) === opt.value}
                      onChange={() => setForm((p) => ({ ...p, isHomeShow: Number(opt.value) }))}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-grid form-grid--2">
              <div className="form-field">
                <span className="form-label">業務分類</span>
                {isEdit || form.source === 'SPIDER' ? (
                  <div>{formatNewsCategory(form.category)}</div>
                ) : (
                  <select
                    id="newsCategory"
                    value={form.category}
                    onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                  >
                    {CREATE_CATEGORY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="form-field">
                <span className="form-label">數據源</span>
                <div>{formatNewsProvider(form.provider)}</div>
              </div>
            </div>

            <div className="form-field">
              <span className="form-label">來源</span>
              <div>{formatNewsSource(form.source)}</div>
            </div>

            {form.details.map((detail, index) => (
              <section key={detail.lang} className="news-lang-section">
                <h4>語系：{detail._label || detail.lang}</h4>
                <div className="form-field">
                  <label htmlFor={`title-${detail.lang}`}>標題（{detail._label || detail.lang}）</label>
                  <input
                    id={`title-${detail.lang}`}
                    type="text"
                    value={detail.title || ''}
                    onChange={(e) => updateDetail(index, 'title', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <span className="form-label">新聞資訊小圖（{detail._label || detail.lang}）</span>
                  <div className="news-image-upload">
                    {detail.image ? (
                      <img src={resolveAdminMediaUrl(detail.image)} alt="" className="news-thumb-preview" />
                    ) : (
                      <div className="news-thumb-placeholder">未設定</div>
                    )}
                    <label className="btn btn-secondary">
                      上傳圖片
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => handleImageUpload(index, e)}
                      />
                    </label>
                  </div>
                </div>
                <div className="form-field">
                  <span className="form-label">內容（{detail._label || detail.lang}）</span>
                  <RichTextEditor
                    key={`${newsId || 'new'}-${detail.lang}`}
                    value={detail.body || ''}
                    onChange={(value) => updateDetail(index, 'body', value)}
                  />
                </div>
              </section>
            ))}

            {error ? <p className="form-message form-message--error">{error}</p> : null}

            <div className="modal-panel__footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
                取消
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? '保存中…' : '保存'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
